import React, { useEffect, useState } from "react";

import CancelIcon from "@mui/icons-material/Cancel";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LockResetIcon from "@mui/icons-material/LockReset";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import HttpsOutlinedIcon from "@mui/icons-material/HttpsOutlined";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ResponsiveGrid from "../grid/responsiveGrid";

import { GridActionsCellItem } from "@mui/x-data-grid";
import { useUserAuth } from "../contexts/authContext";
import { CustomPagination } from "../customPagination";
import { ConvertISOToDate } from "../util";
import { LoginHistory } from "./loginHistory";

export function UserList() {
    const { user, socket, handleFailedFetch, setSnackbar } = useUserAuth();

    const [activeIDs, setActiveIDs] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [sortModel, setSortModel] = useState([{ field: "last_login", sort: "desc" }]);

    useEffect(() => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("api/user/get_all/" + user.user_role + "/" + user.organization_id, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setAllUsers(data))
            .catch((err) => handleFailedFetch(err));
    }, []);

    useEffect(() => {
        if (socket !== null) {
            socket.on("updateActiveUserIDs", (ids) => {
                setActiveIDs(ids);
            });
            socket.emit("getActiveUserIDs");
        }
        return () => {
            if (socket !== null) {
                socket.off("updateActiveUserIDs");
            }
        };
    }, [socket]);

    const userColumns = [
        {
            field: "online",
            headerName: "Online",
            width: 70,
            sortable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            display: "flex",
            renderCell: ({ row }) => {
                return activeIDs.includes(row.id) ? (
                    <Tooltip title="Online">
                        <CheckCircleIcon color="success" fontSize="medium" />
                    </Tooltip>
                ) : (
                    <Tooltip title="Offline">
                        <RadioButtonUncheckedIcon color="disabled" fontSize="medium" />
                    </Tooltip>
                );
            }
        },
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            minWidth: 150,
            valueGetter: (value, row) => {
                return `${row.first_name || ""} ${row.last_name || ""}`;
            }
        },
        {
            field: "email",
            headerName: "Email",
            flex: 1,
            minWidth: 250
        },
        {
            field: "phone_number",
            headerName: "Phone Number",
            flex: 1,
            minWidth: 150
        },
        {
            field: "organization",
            headerName: "Org",
            flex: 1,
            minWidth: 100
        },
        {
            field: "user_role_id",
            headerName: "Role",
            flex: 1,
            minWidth: 150,
            type: "singleSelect",
            editable: true,
            valueOptions:
                user.user_role === "Admin"
                    ? [
                          { label: "Admin", value: 0 },
                          { label: "Admin - Org", value: 3 },
                          { label: "Operations Manager", value: 1 },
                          { label: "Airspace Manager", value: 4 },
                          { label: "First Responder", value: 5 },
                          { label: "Operator", value: 2 }
                      ]
                    : [
                          { label: "Admin - Org", value: 3 },
                          { label: "Operations Manager", value: 1 },
                          { label: "Airspace Manager", value: 4 },
                          { label: "First Responder", value: 5 },
                          { label: "Operator", value: 2 }
                      ],
            /* eslint-disable no-unused-vars */
            valueFormatter: (value, row, column) => {
                const colDef = column;
                const option = colDef.valueOptions.find(({ value: optionValue }) => value === optionValue);
                return option.label;
            }
            /* eslint-disable no-unused-vars */
        },
        {
            field: "last_login",
            headerName: "Last Login",
            minWidth: 250,
            type: "dateTime",
            flex: 1,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "id",
            headerName: "Login History",
            headerAlign: "center",
            disableColumnMenu: true,
            align: "center",
            minWidth: 100,
            sortable: false,
            flex: 1,
            display: "flex",
            renderCell: ({ row }) => {
                return (
                    <IconButton onClick={() => setCurrentUser(row)}>
                        <ListAltIcon />
                    </IconButton>
                );
            }
        },
        {
            field: "pilot",
            headerName: "Pilot",
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            sortable: false,
            minWidth: 100,
            flex: 1,
            display: "flex",
            renderCell: ({ row }) => {
                return <Checkbox checked={row.pilot_uuid !== null} disabled />;
            }
        },
        {
            field: "type",
            headerName: "Active",
            align: "center",
            disableColumnMenu: true,
            headerAlign: "center",
            sortable: false,
            minWidth: 75,
            flex: 1,
            display: "flex",
            renderCell: ({ row }) => {
                return <Checkbox checked={row.active} onClick={() => handleActivateClick(row)} id={`active-${row.email}`} />;
            }
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 125,
            getActions: ({ row }) => {
                return [
                    <Tooltip key={"1"} title={"Click to unlock account"} sx={{ display: row.locked ? "block" : "none" }}>
                        <GridActionsCellItem icon={<HttpsOutlinedIcon />} label="Unlock account" onClick={() => handleUnlockUserButtonClick(row)} />
                    </Tooltip>,
                    <Tooltip key={"2"} title={row.reset_password ? "" : "Reset account password"}>
                        <GridActionsCellItem
                            icon={<LockResetIcon color={row.reset_password ? "error" : "inherit"} />}
                            label="Reset password"
                            onClick={() => handleResetPassword(row)}
                            disabled={row.reset_password === true}
                        />
                    </Tooltip>,
                    <Tooltip key={"3"} title={"Delete user account"}>
                        <GridActionsCellItem id={`delete-${row.email}`} icon={<CancelIcon />} label="Delete account" onClick={() => handleDeleteClick(row)} />
                    </Tooltip>
                ];
            }
        }
    ];

    const handleActivateClick = (row) => {
        const setting = row.active ? "deactivate" : "activate";
        const returnValue = row.active
            ? confirm("This will de-activate the user in the system and user will no longer be able to login. Do you wish to continue?")
            : confirm("This will activate the user in the system. Do you wish to continue?");

        if (!returnValue) return;

        row.active = !row.active;
        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(row)
        };
        fetch(`api/user/${setting}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => setSnackbar({ children: `User successfully ${setting}d`, severity: "success" }))
            .catch((err) => handleFailedFetch(err));
    };

    const handleUnlockUserButtonClick = (user) => {
        if (confirm(`This will unlock ${user.email}'s account. Do you wish to continue?`) === false) {
            return;
        }
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch(`api/user/unlock/${user.id}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                setSnackbar({ children: `User successfully updated`, severity: "success" });

                const updatedUsers = allUsers.map((previousUser) => {
                    return user.id === previousUser.id ? { ...previousUser, locked: !previousUser.locked } : previousUser;
                });
                setAllUsers(updatedUsers);
            })
            .catch((err) => handleFailedFetch(err));
    };

    const handleResetPassword = (row) => {
        if (!confirm("This will force the user to reset their password. Do you wish to continue?")) return;
        if (row.oidc) return setSnackbar({ children: `Unable to update OHID account`, severity: "error" });

        row.reset_password = true;
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(row)
        };
        fetch("api/user/forgot", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => setSnackbar({ children: `User successfully updated`, severity: "success" }))
            .catch((err) => handleFailedFetch(err));
    };

    const handleDeleteClick = (row) => {
        if (!confirm("This delete the user from the system. Do you wish to continue?")) return;

        row.deleted = true;
        setAllUsers(allUsers.filter((r) => r.id !== row.id));

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(row)
        };
        fetch("api/user/delete", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => setSnackbar({ children: `User successfully removed`, severity: "success" }))
            .catch((err) => handleFailedFetch(err));
    };

    // api call to update a certain user
    const updateUser = (updatedUser) => {
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedUser)
        };
        fetch("api/user/update", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => setSnackbar({ children: `User successfully updated`, severity: "success" }))
            .catch((err) => handleFailedFetch(err));
    };

    const updateDatagridRow = (updatedRow, originalRow) => {
        if (originalRow.user_role_id != updatedRow.user_role_id) {
            allUsers.forEach(async (user) => {
                if (user.id === updatedRow.id) {
                    user.user_role_id = updatedRow.user_role_id;
                    await updateUser(user);
                }
            });
        }
        return updatedRow;
    };

    /* istanbul ignore next */
    const handleProcessRowUpdateError = React.useCallback((error) => {
        setSnackbar({ children: error.message, severity: "error" });
    }, []);

    return (
        <Grid size={16} sx={{ maxWidth: "100% !important", p: 3, width: "100%" }}>
            <Typography component="h2" variant="h6" color="#6b778c" noWrap sx={{ flexGrow: 1 }}>
                Manage Users
            </Typography>
            <Paper sx={{ p: 2, mt: 2, display: "flex", flexDirection: "column" }}>
                <Box sx={{ height: 578 }}>
                    <ResponsiveGrid
                        primaryColumns={["name", "email", "user_role_id", "online"]}
                        disableVirtualization
                        rows={allUsers}
                        columns={userColumns}
                        disableRowSelectionOnClick
                        getRowId={(row) => row.id}
                        processRowUpdate={(updatedRow, originalRow) => updateDatagridRow(updatedRow, originalRow)}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        pagination
                        slots={{ pagination: CustomPagination }}
                        initialState={{ pagination: { paginationModel: { pageSize: 9 } } }}
                        sortModel={sortModel}
                        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
                    />
                </Box>
            </Paper>
            <LoginHistory user={currentUser} setUser={setCurrentUser} />
        </Grid>
    );
}
